import { useEffect, useState } from 'react';
import { Panel, PanelHeader, Group, Button, Div, Spinner, Text, Headline, FormItem, Input, Avatar } from '@vkontakte/vkui';
import { useAuthStore } from '../../stores/auth';
import { useWeddingStore } from '../../stores/wedding';
import { useNavigate } from 'react-router-dom';
import { getVKUserProfile, isVKEnvironment, type VKUserProfile } from '../../api/vk';
import { buildVKIdAuthorizeUrl, takeVKIdCallback, takeOAuthHashToken, clearOAuthFromUrl, vkIdLogin, oauthLogin } from '../../api/vkOAuth';
import { readJoinCode, buildVkAppLink } from '../../utils/joinCode';
import type { AuthResponse } from '../../types';

export default function AuthPage() {
  const { login, error, isAuthenticated, setSession } = useAuthStore();
  const { loadWeddings } = useWeddingStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [statusText, setStatusText] = useState<string | null>(null);
  const [vkIdPending, setVkIdPending] = useState(() => localStorage.getItem('vk_id_flow_pending') === '1');
  const [vkUser, setVkUser] = useState<VKUserProfile | null>(null);
  const [isVK] = useState(() => isVKEnvironment());
  const urlJoinCode = readJoinCode() || '';
  const openInVkUrl = buildVkAppLink(urlJoinCode || null);

  useEffect(() => {
    // VK ID code return (browser) — consume at most once
    const cb = takeVKIdCallback();
    if (cb) {
      clearOAuthFromUrl();
      setLoading(true);
      vkIdLogin(cb.code, cb.device_id)
        .then(async (res) => {
          localStorage.removeItem('vk_id_flow_pending');
          setVkIdPending(false);
          // Must update the store BEFORE navigate, else ProtectedRoute bounces to /auth
          setSession(res as AuthResponse);
          try { await loadWeddings(); } catch { /* non-fatal */ }
          navigate('/', { replace: true });
        })
        .catch((e) => {
          console.error('VK ID login failed:', e);
          setStatusText('Не удалось завершить вход через VK ID. Попробуйте ещё раз или откройте приложение в ВКонтакте.');
        })
        .finally(() => setLoading(false));
      return;
    }

    // legacy implicit token
    const hashTok = takeOAuthHashToken();
    if (hashTok) {
      clearOAuthFromUrl();
      setLoading(true);
      oauthLogin(hashTok.access_token, Number(hashTok.user_id))
        .then(async (res) => {
          setSession(res as AuthResponse);
          try { await loadWeddings(); } catch { /* non-fatal */ }
          navigate('/', { replace: true });
        })
        .catch((e) => console.error('OAuth login failed:', e))
        .finally(() => setLoading(false));
      return;
    }

    let cancelled = false;
    getVKUserProfile()
      .then((user) => {
        if (!cancelled && user) {
          setVkUser(user);
          setName(`${user.first_name} ${user.last_name || ''}`.trim());
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [loadWeddings, navigate, setSession]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleLogin() {
    setLoading(true);
    try {
      if (vkUser?.raw_query) {
        await login({ init_data: vkUser.raw_query });
      } else {
        await login({ name: name || 'guest' });
      }
      try {
        await loadWeddings();
      } catch {
        /* non-fatal */
      }
      navigate('/', { replace: true });
    } catch (e) {
      console.error('Login error:', e);
      setStatusText('Не удалось войти. Попробуйте ещё раз или откройте приложение в ВКонтакте.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Panel>
      <PanelHeader><span className="cd-header-brand">Colibri</span></PanelHeader>
      <Group>
        <Div style={{ textAlign: 'center', padding: '40px 16px' }}>
          <Headline level="1" style={{ marginBottom: 8 }}>
            🦋 Colibri
          </Headline>
          <Text style={{ marginBottom: 24, color: 'var(--vkui--color_text_secondary)' }}>
            Свадебный планировщик
          </Text>

          {error && (
            <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 16 }}>
              {error}
            </Text>
          )}
          {statusText && (
            <Text style={{ color: 'var(--vkui--color_text_negative)', marginBottom: 16 }}>
              {statusText}
            </Text>
          )}

          {vkUser ? (
            <Div style={{ marginBottom: 16 }}>
              {vkUser.avatar_url && (
                <Avatar size={72} src={vkUser.avatar_url} style={{ margin: '0 auto 12px' }} />
              )}
              <Headline level="2">{vkUser.first_name} {vkUser.last_name}</Headline>
              <Text style={{ color: 'var(--vkui--color_text_secondary)', fontSize: 13 }}>
                VK ID: {vkUser.vk_id}
              </Text>
            </Div>
          ) : (
            <FormItem top="Ваше имя">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: Анна"
              />
            </FormItem>
          )}

          {loading ? (
            <Spinner size="l" />
          ) : (
            <>
              {!isVK && (
                <>
                  <Button
                    size="l"
                    mode="primary"
                    stretched
                    style={{ marginBottom: 8 }}
                    onClick={() => {
                      window.location.href = openInVkUrl;
                    }}
                  >
                    Открыть в ВКонтакте
                  </Button>
                  <Button
                    size="l"
                    mode="secondary"
                    stretched
                    style={{ marginBottom: 8 }}
                    onClick={async () => {
                      if (loading) return;
                      setLoading(true);
                      setStatusText(null);
                      try {
                        const url = await buildVKIdAuthorizeUrl();
                        localStorage.setItem('vk_id_flow_pending', '1');
                        setVkIdPending(true);
                        window.location.href = url;
                      } catch (e) {
                        console.error('VK ID authorize URL failed:', e);
                        setStatusText('Не удалось открыть страницу входа VK ID.');
                        setLoading(false);
                      }
                    }}
                  >
                    Войти через VK ID
                  </Button>
                  {vkIdPending && (
                    <>
                      <Text style={{ marginBottom: 8, fontSize: 13, color: 'var(--vkui--color_text_secondary)' }}>
                        Если VK показал «Вернитесь в браузер» — вернитесь в браузер и нажмите «Проверить вход».
                      </Text>
                      <Button
                        size="m"
                        mode="tertiary"
                        stretched
                        style={{ marginBottom: 8 }}
                        onClick={() => window.location.reload()}
                      >
                        Проверить вход
                      </Button>
                    </>
                  )}
                </>
              )}
              <Button
                size="l"
                mode={isVK ? 'primary' : 'secondary'}
                onClick={handleLogin}
                stretched
              >
                {vkUser ? 'Начать' : isVK ? 'Войти' : 'Демо-вход'}
              </Button>
            </>
          )}

          <Text style={{ marginTop: 16, fontSize: 12, color: 'var(--vkui--color_text_secondary)' }}>
            {isVK ? 'Вход через ВКонтакте' : 'Войдите через VK или в демо-режиме'}
          </Text>
        </Div>
      </Group>
    </Panel>
  );
}
